/**
 * # Router service
 * > manage our routes (pages)
 *
 * - use routes.json
 * - may code split / async load with `() => import(/* webpackChunkName: "demo" * / 'components/pages/Demo/Home')``
 *
 *  @todo :
 * - stop using local within name - prefere a smart service to just bind the locale with default one if not specified
 *
 * @author Nicolas Husson <nicolas@kffein.com>
 */
import { assign, camelCase, flatten, /* fromPairs, */ get, map, without } from 'lodash';
import { store as appStore } from 'plugins/store/app';
import { byId } from 'src/config/locales';
import { RouteRecordRaw, Router, START_LOCATION, createRouter, createWebHistory } from 'vue-router';
import pages, { Page404 } from './pages';
import customRoutes from './utils/custom_routes';

// /**
//  * @argument {Array} routes - put debug / default routes here
//  * when calling /api/globals, we may add new routes
//  */
// const defaultRoutes: RouteRecordRaw[] = [
//   {
//     path: '/',
//     redirect: { name: `home` },
//   },
// ];

let router:Router
export function useRouter(){
  if(router) return router
  
  /** use our routes.json */
  // const routesMap = routesJson as RouteMapEntry[]
  /** or use our craft_routes.json */
  const routesMap = []; //useRoutesCraft(payload as RouteCraftMain)

  // add custom routes
  if (customRoutes && customRoutes.length) {
    routesMap.push(...customRoutes as RouteMapEntry[])
  }

  let routes = without(flatten(
    map(routesMap, (route) => processRoute(route)),
  ), undefined) as RouteRecordRaw[];

  
  // Add / for 1st level routes paths - else not working
  routes = map(routes, (route) => {
    route.path = `/${route.path}`;
    return route;
  });
  // routes.push(...defaultRoutes);

  /**
   * @argument {VueRouter} router - our vue router instance
   */
  router = createRouter({
    // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
    history: createWebHistory(),
    routes
  })
  return router
}

export default useRouter

// may run consent check
function runConsentCheck(){
  window.Cookiebot?.runScripts?.()
}

/** return locale (name) param based on url */
export function observeFirstLocale(callback: () => void): void {
  router.beforeEach((to, from) => {
    runConsentCheck()

    if (to.matched.length <= 0) {
      appStore.setIs404();
    } else {
      appStore.setIsNot404();
    }

    if (from === START_LOCATION) {
     
      // if guess a local - continue our flow (main.ts)
      callback();
    }
  })

  router.afterEach(() => {
    runConsentCheck()
  })
}

/** resolve route UNLESS we know il will fail (avoid unecessary warning)
 * - if path is external url => we know it will fail
 */
export function tryResolve(path:any){
  if (typeof path === 'string'
    && path.startsWith('http')
    && !path.startsWith(location.origin)) return false

  return router.resolve(path)
}

//* *********** PRIVATE */

/**
 * return component by route handle
 * @param {String} id - route handle
 * @returns {VueComponent}
 */
function getComponentById(id: string): AsyncImportVue {
  // if (pages.hasOwnProperty(id)) { // eslint-disable-line no-prototype-builtins
  if (Object.prototype.hasOwnProperty.call(pages, camelCase(id))) {
    return (pages as any)[camelCase(id)];
  }
  return Page404
}

/**
 * @returns {Object[]} routes
 * @param {Object} route shema (like route.json)
 * @param {String} parentLocale - if defined, ignore other locales for that route - because setted on its parent
 * compute our routes array
 * by looping our routes.json within all our locales
 *
 * Attention - this config aim to be compatible with multi ou single brand
 * so may skip some routes deppending
 * /:brand/library may be /library if only 1 brand
 */
function processRoute(route: RouteMapEntry, parentLocale: string | null = null) {
  const {
    id, sites, meta, section, entry_type, name, component
  } = route;

  // by default, route aren't public
  // const auth = {meta} ?? false

  // because a route has many locales, we create one per locale
  // return map(urls, (url, locale) => {
  return map(sites, (site) => {
    const locale = byId(site.id)?.name;
    // if child route, ignore other local than the one of its parent
    if (parentLocale && parentLocale !== locale) {
      return undefined;
    }
    // retrieve our component within the id (handle)
    const cpn = component ?? getComponentById(name || section as string);
    if (!cpn) { return undefined; }

    // may recursive children
    const children = without(flatten(
      map(get(route, 'children', []), (childRoute) => processRoute(childRoute, locale)),
    ), undefined) as RouteRecordRaw[];

    const path = `${parentLocale ? '/' : ''}${site.url}`;
    
    // give us the chance to still have some unique route without any ids, can make our life simpler for our basic pages.
    // const regex_channel = new RegExp(/(channel_)/gi);
    // const isChannel = regex_channel.test(section);

    // finaly, this is ONE route
    const finalRoute: RouteRecordRaw = {
      name: `${name || section}`,
      // name: `${entry_type}.${locale}`,
      path,
      children,
      // mix lots of useful metas
      meta: assign({}, meta, {
        locale,
        slug: site.slug,
        title: site.title,
        section,
        name,
        entry_type,
        id, // handle
        // auth,
        // child: !!parentLocale
      }),
      component: cpn,
    };
    return finalRoute;
  });
}
